import { queryClient } from '@/lib/trpc'
import { trpc, trpcClient } from '@/lib/trpc'
import { FolderIcon } from 'lucide-react'
import { IntegrationProvider } from '../types'
import { LocalFileConfigModal } from './LocalFileConfigModal'

// Create a wrapper component for the Folder icon that satisfies the type system
const FolderIconWrapper = () => <FolderIcon className='h-5 w-5' />

/**
 * Local File integration provider implementation
 * This provider is only available in desktop environment
 */
export const LocalFileProvider: IntegrationProvider = {
  id: 'local-file',
  type: 'local-file',
  name: 'Local Documentation',
  description: 'Connect local documentation files from your computer (Desktop only).',

  // Use our wrapper component for the icon
  Icon: FolderIconWrapper,

  // Configuration modal component
  ConfigModal: LocalFileConfigModal,

  // Check if the provider should be shown
  shouldDisplay: () => {
    return Boolean(window._main_env?.IS_DESKTOP)
  },

  // Handler to connect to local files
  handleConnect: async ({ workspaceId }) => {
    try {
      // Create the integration directly since we don't need OAuth
      const result = await trpcClient.integrations.create.mutate({
        workspaceId,
        type: 'local-file',
        name: 'Local Documentation',
        metadata: {
          isConfigured: false,
        },
      })

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })

      return result
    } catch (error: unknown) {
      console.error('Error creating local documentation integration:', error)
      throw error
    }
  },

  // Get available features for Local Files
  getFeatures: () => [
    {
      id: 'docusaurus',
      name: 'Docusaurus',
      description: 'Import documentation from local Docusaurus projects',
      enabled: true,
    },
    {
      id: 'vitepress',
      name: 'VitePress',
      description: 'Import documentation from local VitePress projects',
      enabled: true,
    },
    {
      id: 'gitbook',
      name: 'GitBook',
      description: 'Import documentation from local GitBook projects',
      enabled: true,
    },
    {
      id: 'markdown',
      name: 'Markdown/MDX Files',
      description: 'Import general markdown documentation from local files',
      enabled: true,
    },
  ],

  // Sync data from local files
  syncData: async (integration) => {
    try {
      // Sync the documentation
      await trpcClient.integrations.syncIntegration.mutate({
        integrationId: integration.id
      })

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
    } catch (error) {
      console.error('Error syncing local documentation:', error)
    }
  },
}

export default LocalFileProvider 